// THIS MODULE DOES NOT HOLD ANY BACKEND DATA
// EVERYTHING IS VIEWER FRONTEND STATE
// MAIN SOURCE OF SELECTED DATA IS selected.py MODULE
import { zones_from_viewer_bom } from "./viewer_helpers"
import { nameSort } from "/src/utils/basics";
import _ from "lodash"
import { defineStore } from 'pinia'
import { useSelectedStore } from "/src/stores/selected"
import { useCloudstorageStore } from "/src/stores/cloudstorage"
import { useSessionStore } from "./session";
import bus from "/src/utils/event_bus";

// Calls to loadBom so far; only the last one commits its BOM.

function is_ext(cf, ext) {
    return String(cf?.meta?.ext || "").toLowerCase() == ext
}
let bom_calls = 0

export const useViewerStore = defineStore('viewer', {
    state: () => ({
        // Mode
        designs_mode: false,
        dwg_mode: true,
        right_panel_mode: "details",
        click_3D_mode: "selection", // measure, clipping_plane
        level: "assembly",  // part, assembly, module, project (subassembly is also assembly)

        // Viewer state
        left_panel_dwg_number: 1,
        right_panel_cf_uuid: null,
        selected_parts_ifc_ids: [],
        selected_zones: [],

        // Multipanel-group viewer state (only used when level === "multipanel_group")
        enabled_drawing_design_names: [], // design names whose drawings are shown; empty = show all
        left_panel_member_uuid: null,
        selected_panel_uuids: [], // member assembly uuids visible in 3D; empty = all visible


        // IFC.JS DATA
        selected_part_props: {},
        bom_loading_status: false,
        bom: [],
    }),
    getters: {
        resPackage: (state) => {
            // THIS IS NOT OK FOR level "project" BECAUSE YOU HAVE ALL FILES
            // AND YOU NEED TO FILTER TO HAVE ONLY "PROJECT_" FILES

            const cloudstorage_store = useCloudstorageStore()
            let pck = cloudstorage_store.packagesByNameAndLevel({
                name: "resources",
                level: state.level + "_design"
            })?.[0]
            return pck
        },
        heavyMetaPackage: (state) => {
            const cloudstorage_store = useCloudstorageStore()
            let pck = cloudstorage_store.packagesByNameAndLevel({
                name: "heavy_meta",
                level: state.level + "_design"
            })?.[0]
            return pck
        },
        parentResPackage: () => {
            const cloudstorage_store = useCloudstorageStore()
            let pck = cloudstorage_store.packagesByNameAndLevel({
                name: "resources",
                level: "parent_assembly_design"
            })?.[0]
            return pck
        },
        parentHeavyMetaPackage: () => {
            const cloudstorage_store = useCloudstorageStore()
            let pck = cloudstorage_store.packagesByNameAndLevel({
                name: "heavy_meta",
                level: "parent_assembly_design"
            })?.[0]
            return pck
        },
        allPackages: (state) => {
            const cloudstorage_store = useCloudstorageStore()
            return cloudstorage_store.packagesByNameAndLevel({
                name: null,
                level: state.level + "_design"
            })
        },
        allConstPackages: (state) => {
            const cloudstorage_store = useCloudstorageStore()
            return cloudstorage_store.packagesByNameAndLevel({
                name: null,
                level: state.level
            })
        },
        getDrawings(state) {
            let files = this.resPackage?.files || []
            let drawings = files.filter(cf => cf?.meta?.section == state.level + "_drawing")
            let dwg_number_groups = _.groupBy(drawings, "meta.dwg_number")
            let filtered_dwgs = _.map(dwg_number_groups, group => _.maxBy(group, cf => cf.meta?.version ?? 0))
            return filtered_dwgs
        },
        getIFC(state) {
            // By extension too: the model section can hold a BTLx, which the
            // IFC loader must never be handed.
            return this.modelFiles.find(cf => is_ext(cf, "ifc"))
        },
        // The design's BTLx, when its model is one (no IFC beside it: that is
        // refused at upload).
        getBTLx() {
            return this.modelFiles.find(cf => is_ext(cf, "btlx"))
        },
        modelFiles(state) {
            const selected_store = useSelectedStore()
            let files = []
            if (selected_store.isSubassd) files = this.parentResPackage?.files || []
            else files = this.resPackage?.files || []
            // A panel built from a BTLx keeps it in a file group of its own.
            const sections = [state.level + "_ifc", state.level + "_btlx"]
            return files.filter(cf => sections.includes(cf.meta?.section))
        },
        getExtraIFCs(state) {
            const selected_store = useSelectedStore()
            // A subassembly is in its panel's main IFC. The panel's extra IFCs
            // would switch the viewer to multi-IFC mode, which draws no zones,
            // so the subassembly could not be told from the rest of the panel.
            if (selected_store.isSubassd) return []
            const files = this.resPackage?.files || []
            return files.filter(cf => cf.meta.section == "extra_" + state.level + "_ifc")
        },
        multi_ifc_mode() {
            return this.getIFC && this.getExtraIFCs.length > 0
        },
        multi_ifc_layers() {
            return [this.getIFC?.short_name || "", ...this.getExtraIFCs.map(cf => cf.short_name)]
        },
        module_layers() {
            const selected_store = useSelectedStore()
            let design = selected_store.annotatedRouteDesign
            let layers = [...(design?.meta?.assd_names || [])]
            layers.sort(nameSort)
            if (!layers) layers = ["main"]
            return layers
        },
        raw_zones(state) {
            const selected_store = useSelectedStore()
            // Multipanel-group viewer: aggregate zones across all member BOMs.
            if (state.level === 'multipanel_group') {
                const members = selected_store.selected_multipanel_group?.members || []
                const aggregated = []
                for (const m of members) {
                    if (Array.isArray(m.bom)) aggregated.push(...m.bom)
                }
                return zones_from_viewer_bom(aggregated)
            }
            let layers
            let design = selected_store.annotatedRouteDesign
            if (selected_store.isSubassd) {
                layers = zones_from_viewer_bom(
                    state.bom,
                    design?.name
                )
                layers.unshift("PARENT")
            } else {
                layers = zones_from_viewer_bom(state.bom)
            }
            return layers
        },
        zones(state) {
            const session_store = useSessionStore()
            const subzones_from_10 = session_store.subzones_from_10
            if (subzones_from_10) {
                return Object.keys(this.subzone_map).sort((a, b) => Number(a) - Number(b))
            }
            else { return this.raw_zones }
        },
        // Multipanel-group helpers (only meaningful when level === "multipanel_group")
        getMultipanelMembers() {
            const selected_store = useSelectedStore()
            const members = selected_store.selected_multipanel_group?.members || []
            return [...members].sort((a, b) => (a.position || 0) - (b.position || 0))
        },
        getMultipanelMemberUrls() {
            // Returns ALL member URLs (no filtering by selected_zones).
            // Visibility per member is handled by the OBC viewer toggling
            // model.object.visible — the IFCs are loaded once and never reloaded
            // on selection changes.
            const members = this.getMultipanelMembers
            const out = []
            for (const m of members) {
                const cf = (m.files || []).find(f => f?.meta?.section == "assembly_ifc" && is_ext(f, "ifc"))
                if (cf?.presigned_url) {
                    // Append a unique hash fragment per member so the IFC loader
                    // gets a distinct identifier even when several members share
                    // a design (and therefore the same presigned IFC URL).
                    // The fragment is not sent to the server, so fetch still
                    // resolves to the same blob.
                    const url = cf.presigned_url + `#mp-${m.assembly_uuid}`
                    out.push({
                        url,
                        member_uuid: m.assembly_uuid,
                        display_name: m.display_name,
                        gap_before_mm: m.gap_before_mm || 0,
                        width_mm: m.width_mm || 0,
                    })
                }
            }
            return out
        },
        getMultipanelDrawings() {
            const selected_store = useSelectedStore()
            const out = []

            // Group-level drawings — auto-dwgs generated for the whole MultipanelGroup
            // (separate from per-member assembly drawings).
            const mpg = selected_store.selected_multipanel_group
            const group_files = mpg?.group_files || []
            const group_drawings = group_files.filter(f => f?.meta?.section == "assembly_drawing")
            const group_dwg_groups = _.groupBy(group_drawings, "meta.dwg_number")
            const group_label = mpg?.group?.name || "Group"
            for (const [dwg_number, files] of Object.entries(group_dwg_groups)) {
                const cf = _.maxBy(files, c => c.meta?.version ?? 0)
                if (!cf) continue
                out.push({
                    ...cf,
                    member_uuid: null,
                    member_display_name: group_label,
                    design_name: group_label,
                    dwg_number: Number(dwg_number),
                    dwg_name: cf.meta?.dwg_name || "",
                })
            }

            // Per-member drawings — members of the same design share identical
            // drawings, so dedupe by (design_name, dwg_number). The first member
            // encountered (in position order) becomes the canonical owner.
            const members = this.getMultipanelMembers
            const seen = new Set()
            for (const m of members) {
                const drawings = (m.files || []).filter(f => f?.meta?.section == "assembly_drawing")
                const dwg_number_groups = _.groupBy(drawings, "meta.dwg_number")
                for (const [dwg_number, files] of Object.entries(dwg_number_groups)) {
                    const cf = _.maxBy(files, c => c.meta?.version ?? 0)
                    if (!cf) continue
                    const dedup_key = `${m.design_name}:${dwg_number}`
                    if (seen.has(dedup_key)) continue
                    seen.add(dedup_key)
                    out.push({
                        ...cf,
                        member_uuid: m.assembly_uuid,
                        member_display_name: m.display_name,
                        design_name: m.design_name,
                        dwg_number: Number(dwg_number),
                        dwg_name: cf.meta?.dwg_name || "",
                    })
                }
            }
            return out
        },
        getMultipanelDetails() {
            const members = this.getMultipanelMembers
            const by_short_name = {}
            for (const m of members) {
                const dets = (m.files || []).filter(f => f?.meta?.section == "detail")
                for (const cf of dets) {
                    by_short_name[cf.short_name] = cf
                }
            }
            return Object.values(by_short_name)
        },
        getMultipanelDocs() {
            const members = this.getMultipanelMembers
            const by_short_name = {}
            for (const m of members) {
                const docs = (m.files || []).filter(f => f?.meta?.section == "doc")
                for (const cf of docs) {
                    by_short_name[cf.short_name] = cf
                }
            }
            return Object.values(by_short_name)
        },
        findMultipanelMemberFile() {
            return (uuid) => {
                if (!uuid) return null
                const members = this.getMultipanelMembers
                for (const m of members) {
                    const cf = (m.files || []).find(f => f?.uuid == uuid)
                    if (cf) return cf
                }
                return null
            }
        },
        subzone_map(state) {
            const session_store = useSessionStore()
            const subzones_from_10 = session_store.subzones_from_10
            let map = {}
            this.raw_zones.forEach(z => {
                // Determine zone and subzone for the map
                let subzone = z
                let zone
                if (subzones_from_10 && Math.abs(z) >= 10) {
                    if (Number(z) >= 10) {
                        zone = Math.floor(Number(z) / 10)
                    } else if (Number(z) <= -10) {
                        zone = Math.ceil(Number(z) / 10)
                    }
                } else {
                    zone = z
                }

                // Add to map
                if (!map[zone]) {
                    map[zone] = []
                }
                map[zone].push(subzone)
            })
            return map
        },
    },
    actions: {
        setDesignsMode(dm) { this.designs_mode = dm },
        set3DMode() { this.dwg_mode = false },
        setDWGMode() { this.dwg_mode = true },
        setLevel(level) { this.level = level },
        setLeftPanelDWGNumber(idx) { this.left_panel_dwg_number = idx },
        setRightPanelMode(data) { this.right_panel_mode = data },
        setClick3DMode(data) { this.click_3D_mode = data },
        setRightPanelCFUuid(uuid) { this.right_panel_cf_uuid = uuid },
        setSelectedPartsUuids(data) { this.selected_parts_ifc_ids = data.filter(v => !!v) },
        setSelectedPartProps(data) { this.selected_part_props = data },
        setSelectedZones(zones) { this.selected_zones = zones },
        setEnabledDrawingDesignNames(names) { this.enabled_drawing_design_names = names },
        setSelectedPanelUuids(uuids) { this.selected_panel_uuids = uuids },
        setLeftPanelMemberUuid(uuid) { this.left_panel_member_uuid = uuid },
        setBomLoadingStatus(status) { this.bom_loading_status = status },
        setBom(bom) { this.bom = bom },

        async loadBom() {
            const selected_store = useSelectedStore()
            // Only the last call commits: a slow BOM of the design shown before
            // must not land on the one shown now.
            const call = ++bom_calls
            this.setBomLoadingStatus(true)
            // Pick cloudfile
            let files = []
            if (selected_store.isSubassd) {
                files = this.parentHeavyMetaPackage?.files || []
            } else {
                files = this.heavyMetaPackage?.files || []
            }
            let cf = files.reduce(function (acc, obj) {
                if (obj.name == "viewer_bom" && obj.meta?.version > (acc?.meta?.version || -1)) {
                    return obj;
                } else {
                    return acc;
                }
            }, null);

            // No BOM yet (a subassembly's panel packages may still be on their
            // way): clear the one of the design shown before and stop loading.
            // Returning early left both, with the progress bar on for good.
            if (!cf) {
                this.setBom([])
                this.setBomLoadingStatus(false)
                return
            }

            // Download JSON. The slim list loader skips presign generation,
            // so cf.presigned_url may be empty — resolve via the store helper.
            const cloudstorage_store = useCloudstorageStore()
            const url = await cloudstorage_store.getPresignedUrl(cf)
            if (call !== bom_calls) return
            if (!url) {
                this.setBom([])
                this.setBomLoadingStatus(false)
                return
            }
            console.log("[i] Downloading VIEWER BOM JSON.")
            const is_subassd = selected_store.isSubassd
            fetch(url)
                .then(r => {
                    // An error body (an expired URL answers XML) is not a BOM.
                    if (!r.ok) throw new Error(`BOM fetch failed with status ${r.status}`)
                    return r.json()
                })
                .then(bom => {
                    if (call !== bom_calls) return
                    this.setBom(bom)
                    // A subassembly's zones come from its panel's BOM, which
                    // arrives after its own packages: pick the station's zones
                    // again now that they exist.
                    if (is_subassd) bus.emit('events/viewer/default_zone_selection')
                })
                .catch(e => {
                    // The drawing and the model still show; only the part
                    // list stays empty until the panel is opened again.
                    console.warn(`[!] Cant load viewer BOM: ${e}`)
                    if (call === bom_calls) this.setBom([])
                })

            this.setBomLoadingStatus(false)
        },

    },
})
